import { useMutation, useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  BusinessType,
  CategoriesModel,
  CategoriesModelDto,
  TransactionModel,
  TransactionModelDto,
  TransactionFormInterface,
  TransactionFormInterfaceDto,
  PaginationModelDto,
  PaginationModel,
  WidgetsModelDto,
  WidgetsModel,
  PeriodModelDto,
  PeriodModel,
} from "@interfaces/index";
import {
  formatDateForApi,
  ASSET_NO_AGREEMENT_ATTACHMENTS_NAME,
  ASSET_NO_INVOICE_ATTACHMENTS_NAME,
} from "@utils/index";

const prepareTransactionToModel = (
  transaction: TransactionModelDto,
): TransactionModel => ({
  uniqueId: transaction.unique_id,
  type: transaction.type,
  date: transaction.date,
  userBusiness: {
    uniqueId: transaction.user_business.unique_id,
    name: transaction.user_business.name,
    type: transaction.user_business.type,
    tradingStartDate: transaction.user_business.trading_start_date,
  },
  category: {
    uniqueId: transaction.category.unique_id,
    title: transaction.category.title,
    type: transaction.category.type,
    nominalCode: transaction.category.nominal_code,
  },
  subcategory: {
    uniqueId: transaction.sub_category.unique_id,
    title: transaction.sub_category.title,
    type: transaction.sub_category.type,
    nominalCode: transaction.sub_category.nominal_code,
    transactionDescriptionRequired:
      transaction.sub_category.transaction_description_required || false,
    assetRequired: transaction.sub_category.asset_required,
    hideFromAssetTypes: transaction.sub_category.hide_from_asset_types,
  },
  asset: !transaction.asset
    ? undefined
    : {
        uniqueId: transaction.asset.unique_id,
        name: transaction.asset.name,
        type: transaction.asset.type,
        purchaseType: transaction.asset.purchase_type,
        date: transaction.asset.date,
        firstLetAt: transaction.asset.first_let_at,
        cost: transaction.asset.cost,
        jointShare: transaction.asset.joint_share,
        disposal: transaction.asset.disposal,
        policies: transaction.asset.policies,
        uploadLaterAgreement: transaction.asset.attachments?.some(
          ({ type, upload_later, original_name }) =>
            type === "loan_agreement" &&
            (original_name === null ||
              original_name === ASSET_NO_AGREEMENT_ATTACHMENTS_NAME) &&
            upload_later,
        ),
        notApplicableAgreement: transaction.asset.attachments?.some(
          ({ type, not_applicable, original_name }) =>
            type === "loan_agreement" &&
            (original_name === null ||
              original_name === ASSET_NO_AGREEMENT_ATTACHMENTS_NAME) &&
            not_applicable,
        ),
        uploadLaterInvoice: transaction.asset.attachments?.some(
          ({ type, upload_later, original_name }) =>
            type === "invoice" &&
            (original_name === null ||
              original_name === ASSET_NO_INVOICE_ATTACHMENTS_NAME) &&
            upload_later,
        ),
        notApplicableInvoice: transaction.asset.attachments?.some(
          ({ type, not_applicable, original_name }) =>
            type === "invoice" &&
            (original_name === null ||
              original_name === ASSET_NO_INVOICE_ATTACHMENTS_NAME) &&
            not_applicable,
        ),
      },
  period: {
    title: transaction.period.title,
    status: {
      value: transaction.period.status.value,
      label: transaction.period.status.label,
    },
    startDate: transaction.period.start_date,
    endDate: transaction.period.end_date,
  },
  attachments: transaction.attachments.map((attachment) => ({
    uniqueId: attachment.unique_id,
    originalName: attachment.original_name,
    url: attachment.url,
    size: attachment.size,
  })),
  amount: transaction.amount,
  status: {
    value: transaction.status.value,
    label: transaction.status.label,
  },
  submissionStatus: {
    value: transaction.submission_status.value,
    label: transaction.submission_status.label,
  },
  description: transaction.description,
  refund: transaction.refund,
  refundOf: transaction.refund_of
    ? {
        uniqueId: transaction.refund_of.unique_id,
        amount: transaction.refund_of.amount,
        date: transaction.refund_of.date,
      }
    : null,
  policies: {
    canUpdate: transaction.policies.canUpdate,
    canDelete: transaction.policies.canDelete,
  },
  externalCategoryTitle: transaction.external_category,
  isImported: !!transaction.is_imported,
  disallowed: !!transaction.disallowed,
});

export const useTransactionGetCategories = (
  type?: BusinessType,
  isTypeRequired?: boolean,
) => {
  const queryFn = async () => {
    const params = type ? `?user_business_type=${type}` : "";
    const { data: dataDto } = await api.get<CategoriesModelDto>(
      `/categories${params}`,
    );
    const data: CategoriesModel = {
      income: dataDto.income.map((category) => ({
        uniqueId: category.unique_id,
        title: category.title,
        type: category.type,
        nominalCode: category.nominal_code,
        subcategories: category.categories.map((subcategory) => ({
          uniqueId: subcategory.unique_id,
          title: subcategory.title,
          type: subcategory.type,
          nominalCode: subcategory.nominal_code,
          transactionDescriptionRequired:
            subcategory.transaction_description_required,
          assetRequired: subcategory.asset_required,
          hideFromAssetTypes: subcategory.hide_from_asset_types,
          hardMaxAmount: subcategory.hard_max_amount,
          softMaxAmount: subcategory.soft_max_amount,
          softMinAmount: subcategory.soft_min_amount,
          hardMaxAmountMessage: subcategory.hard_max_amount_message,
          softMaxAmountMessage: subcategory.soft_max_amount_message,
          softMinAmountMessage: subcategory.soft_min_amount_message,
        })),
      })),
      expense: dataDto.expense.map((category) => ({
        uniqueId: category.unique_id,
        title: category.title,
        type: category.type,
        nominalCode: category.nominal_code,
        subcategories: category.categories.map((subcategory) => ({
          uniqueId: subcategory.unique_id,
          title: subcategory.title,
          type: subcategory.type,
          nominalCode: subcategory.nominal_code,
          transactionDescriptionRequired:
            subcategory.transaction_description_required,
          assetRequired: subcategory.asset_required,
          hideFromAssetTypes: subcategory.hide_from_asset_types,
          hardMaxAmount: subcategory.hard_max_amount,
          softMaxAmount: subcategory.soft_max_amount,
          softMinAmount: subcategory.soft_min_amount,
          hardMaxAmountMessage: subcategory.hard_max_amount_message,
          softMaxAmountMessage: subcategory.soft_max_amount_message,
          softMinAmountMessage: subcategory.soft_min_amount_message,
        })),
      })),
    };

    return data;
  };

  return useQuery<CategoriesModel, ApiErrorModel>({
    queryKey: ["transactionGetCategories", type],
    queryFn,
    enabled: !isTypeRequired || (isTypeRequired && !!type),
  });
};

export const useTransactionGetAll = ({
  isSearchUsing,
  search = "",
  submissionId = "",
  businessId = "",
  assetId = "",
  type = "",
  categoryId = "",
  status = "",
  attachment = "",
  refund = "",
  startDate = "",
  endDate = "",
  page,
  perPage,
}: {
  isSearchUsing?: boolean;
  search?: string;
  submissionId?: string;
  businessId?: string;
  assetId?: string;
  type?: string;
  categoryId?: string;
  status?: string;
  attachment?: string;
  refund?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  perPage?: string;
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        search,
        submission_unique_id: submissionId,
        user_business_unique_id: businessId,
        asset_unique_id: assetId,
        type,
        category_unique_id: categoryId,
        is_open:
          status === "open" ? "1" : status === "closed" ? "0" : undefined,
        has_attachments: attachment === "" ? undefined : attachment,
        is_refund: refund === "" ? undefined : refund,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
        page,
        per_page: perPage,
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<{
      data: TransactionModelDto[];
      meta: PaginationModelDto;
    }>(`/transactions?${params}`);

    const transactions = dataDto.data.map((transaction) =>
      prepareTransactionToModel(transaction),
    );
    const pagination: PaginationModel = {
      total: dataDto.meta.total,
      perPage: dataDto.meta.per_page,
    };

    return {
      transactions,
      pagination,
    };
  };

  return useQuery<
    {
      transactions: TransactionModel[];
      pagination: PaginationModel;
    },
    ApiErrorModel
  >({
    queryKey: [
      "transactionGetAll",
      isSearchUsing,
      search,
      submissionId,
      businessId,
      assetId,
      type,
      categoryId,
      status,
      attachment,
      refund,
      startDate,
      endDate,
      page,
      perPage,
    ],
    queryFn,
    refetchOnWindowFocus: true,
    enabled:
      (!isSearchUsing && (!!businessId || !!submissionId)) ||
      (!!isSearchUsing && !!search),
  });
};

export const useTransactionGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<TransactionModelDto>(
      `/transactions/${id}`,
    );

    return prepareTransactionToModel(dataDto);
  };

  return useQuery<TransactionModel, ApiErrorModel>({
    queryKey: ["transactionGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useTransactionCreate = (
  onSuccess?: (pressedCreateNewBtn: boolean) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    businessId,
    categoryId,
    assetId,
    date,
    amount,
    description,
    files,
    refund,
    refundOf,
  }: TransactionFormInterface) => {
    const fieldsDto: TransactionFormInterfaceDto = {
      user_business_unique_id: businessId,
      sub_category_unique_id: categoryId,
      asset_unique_id: assetId === "not_asset_specific" ? null : assetId,
      date: formatDateForApi(date),
      amount,
      description,
      refund,
      refund_of: refundOf,
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post("/transactions", fieldsDto);
  };

  return useMutation({
    mutationKey: ["transactionCreate"],
    mutationFn,
    onSuccess: (_, { pressedCreateNewBtn }) => {
      if (onSuccess) {
        onSuccess(!!pressedCreateNewBtn);
      }
    },
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        categoryId: error.fields?.sub_category_unique_id || "",
        date: error.fields?.date || "",
        amount: error.fields?.amount || "",
        description: error.fields?.description || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useTransactionUpdate = (
  onSuccess?: (pressedCreateNewBtn: boolean) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    transactionId,
    businessId,
    categoryId,
    assetId,
    date,
    amount,
    description,
    refund,
    refundOf,
    files,
    attachments,
  }: TransactionFormInterface) => {
    const fieldsDto: TransactionFormInterfaceDto = {
      user_business_unique_id: businessId,
      sub_category_unique_id: categoryId,
      asset_unique_id: assetId === "not_asset_specific" ? null : assetId,
      date: formatDateForApi(date),
      amount,
      description,
      refund,
      refund_of: refundOf,
      attachments: attachments?.map((attachment) => ({
        unique_id: attachment.uniqueId,
      })),
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.put(`/transactions/${transactionId}`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["transactionUpdate"],
    mutationFn,
    onSuccess: (_, { pressedCreateNewBtn }) => {
      if (onSuccess) {
        onSuccess(!!pressedCreateNewBtn);
      }
    },
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        categoryId: error.fields?.sub_category_unique_id || "",
        date: error.fields?.date || "",
        amount: error.fields?.amount || "",
        description: error.fields?.description || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useTransactionGetWidgets = ({
  businessId = "",
  startDate = "",
  endDate = "",
  assetId = "",
}: {
  businessId?: string;
  startDate?: string;
  endDate?: string;
  assetId?: string;
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        user_business_unique_id: businessId,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
        asset_unique_id: assetId,
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<WidgetsModelDto>(
      `/dashboard?${params}`,
    );
    const data: WidgetsModel = {
      totals: {
        totalIncome: dataDto.totals.total_income,
        totalExpense: dataDto.totals.total_expense,
        totalProfit: dataDto.totals.total_profit,
        mileage: dataDto.totals.mileage,
        totalBusiness: dataDto.totals.total_business,
        totalPrivate: dataDto.totals.total_private,
      },
      categories: dataDto.categories.map((category) => ({
        uniqueId: category.unique_id,
        title: category.title,
        type: category.type,
        transactionsCount: category.transactions_count,
        totalTransactionsAmount: category.total_transactions_amount,
        business: category.business,
        private: category.private,
        assets: category.assets.map((asset) => ({
          uniqueId: asset.unique_id,
          private: asset.private,
          business: asset.business,
          name: asset.name,
          categories: asset.categories.map((subcategory) => ({
            uniqueId: subcategory.unique_id,
            title: subcategory.title,
            type: subcategory.type,
            totalTransactionsAmount: subcategory.total_transactions_amount,
          })),
        })),
      })),
      mileage: dataDto.mileage && {
        items: dataDto.mileage.items.map((item) => ({
          assetUniqueId: item.asset_unique_id,
          assetName: item.asset_name,
          totalAmount: item.total_amount,
          business: item.business,
          private: item.private,
          mileageAmount: item.mileage_amount,
          mileageCost: item.mileage_cost,
          privateMiles: item.private_miles,
          businessMiles: item.business_miles,
          totalMiles: item.total_miles,
        })),
        summary: {
          totalBusiness: dataDto.mileage.summary.total_business,
          totalPrivate: dataDto.mileage.summary.total_private,
          generalTotalAmount: dataDto.mileage.summary.general_total_amount,
          totalBusinessMiles: dataDto.mileage.summary.total_business_miles,
          totalMileageCost: dataDto.mileage.summary.total_mileage_cost,
          businessMiles: dataDto.mileage.summary.business_miles,
          privateMiles: dataDto.mileage.summary.private_miles,
          totalMiles: dataDto.mileage.summary.total_miles,
        },
      },
    };

    return data;
  };

  return useQuery<WidgetsModel, ApiErrorModel>({
    queryKey: [
      "transactionGetWidgets",
      businessId,
      startDate,
      endDate,
      assetId,
    ],
    queryFn,
    refetchOnWindowFocus: true,
    enabled: !!businessId,
  });
};

export const useTransactionFindPeriod = ({ date = "" }: { date?: string }) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<PeriodModelDto>(
      `/transactions/find-period/${date}`,
    );
    const data: PeriodModel = {
      title: dataDto.title,
      status: {
        label: dataDto.status.label,
        value: dataDto.status.value,
      },
      startDate: dataDto.start_date,
      endDate: dataDto.end_date,
    };

    return data;
  };

  return useQuery<PeriodModel, ApiErrorModel>({
    queryKey: ["transactionFindPeriod", date],
    queryFn,
    refetchOnWindowFocus: true,
    retry: false,
    enabled: !!date,
  });
};

export const useTransactionDelete = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async (id: string) =>
    await api.delete(`/transactions/${id}`);

  return useMutation({
    mutationKey: ["transactionDelete"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};
